import Component from '@glimmer/component';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { Button } from 'tio-ui/components/buttons';
import { on } from '@ember/modifier';
import type { OutputData, ToolConstructable } from '@editorjs/editorjs';
import type LpnPage from '../../models/lpn-page';

interface FeaturedSchoolEditorArgs {
  onSave?: (data: OutputData) => void;
  savedData?: OutputData;
}

interface LpnAdditionalInfoEditorArgs {
  onSave?: (data: OutputData, lpn: LpnPage) => void;
  savedData?: OutputData;
  lpn: LpnPage;
}

class FeaturedSchoolEditor extends Component<FeaturedSchoolEditorArgs> {
  editor!: EditorJS;
  @tracked editorData: OutputData | null = null;
  @tracked isReadOnly: boolean = false;
  @tracked savedData: OutputData = this.args.savedData || { blocks: [] };

  save = () => {
    this.editor.save().then((outputData) => {
      this.args.onSave?.(outputData);
    });
  };

  // TODO: Return to this. Seems weird to tie the component to the modifier so explicitly.
  initializeEditor = modifier(() => {
    this.editor = new EditorJS({
      tools: {
        header: Header,
        list: {
          class: List as unknown as ToolConstructable,
          toolbox: [
            {
              title: 'Ordered List',
              data: {
                style: 'ordered',
              },
            },
            {
              title: 'Unordered List',
              data: {
                style: 'unordered',
              },
            },
          ],
        },
      },
      onChange: () => {
        this.save();
      },
      onReady: () => {
        if (this.savedData) {
          this.editor.render(this.savedData);
        }
      },
    });
  });

  <template>
    <div
      class="w-full border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
    >
      <div class="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold text-gray-600 dark:text-gray-300">{{t
            "common.featured_school_editor.add_description"
          }}</h2>
        <div class="p-4">
          <p class="text-gray-600 dark:text-gray-300">{{t
              "common.featured_school_editor.visibility_warning"
            }}</p>
        </div>
      </div>
      <div class="px-4 py-2 bg-white dark:bg-gray-900 rounded-b-lg">
        <div id="editorjs" class="mx-12" {{this.initializeEditor}}></div>
      </div>
    </div>
  </template>
}

class LpnAdditionalInfoEditor extends Component<LpnAdditionalInfoEditorArgs> {
  editor!: EditorJS;
  @tracked editorData: OutputData | null = null;
  @tracked isReadOnly: boolean = false;
  @tracked savedData: OutputData = this.args.savedData || { blocks: [] };

  saveAdditionalInfo = () => {
    this.editor.save().then((outputData) => {
      this.args.onSave?.(outputData, this.args.lpn);
    });
  };

  initializeEditor = modifier(() => {
    this.editor = new EditorJS({
      autofocus: true,
      holder: `editorjs-${this.args.lpn.id}`,
      tools: {
        header: Header,
        list: List,
      },
      onReady: () => {
        if (this.savedData) {
          this.editor.render(this.savedData);
        }
      },
    });
  });

  <template>
    <div
      class="w-full border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
    >
      <div class="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <p class="!mb-0 text-lg font-semibold text-gray-600 dark:text-gray-300">
          {{t "common.lpn_additional_info_editor.add_additional_information"}}
        </p>
      </div>
      <div class="h-[250px] px-4 py-2 bg-white dark:bg-gray-900 rounded-b-lg">
        <div id="editorjs-{{@lpn.id}}" class="mx-12" {{this.initializeEditor}}>
        </div>
      </div>
      <div class="relative flex justify-end m-4">
        <Button @intent="primary" {{on "click" this.saveAdditionalInfo}}>
          {{t "common.lpn_additional_info_editor.save_additional_information"}}
        </Button>
      </div>
    </div>
  </template>
}
export { FeaturedSchoolEditor, LpnAdditionalInfoEditor };
